import { Button } from '@talentmesh/ui/components/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@talentmesh/ui/components/dropdown-menu';
import { TypographySmall } from '@talentmesh/ui/components/typography';
import { ChevronDownIcon } from 'lucide-react';
import { useMemo } from 'react';
import { mapFromCandidateOverviewToCandidateDialogModel } from '../../../../../Dialogs/Dialogs.Utils';
import { DialogEnum, useActionDialogContext } from '../../../../components/dialogs/Context/ActionDialogContext';
import { hiringStatusToString } from '../../../../Utils/Utils';
import TooltipTypography from '../../../Components/TooltipTypography';
import { useCandidatesOverviewContext } from '../../CandidatesOverviewContext/CandidatesOverviewContext';
import { CellParams } from '../utils/useColumns';
import { CellProps } from '.';
import { AssessmentStatuses } from '@/Models/AssessmentData';
import { ApplicantStatus } from '@/Models/CandidateOverview';
import HiringAction from '@/Models/HiringAction';
import isHiringActionAllowedInState from '@/Utils/ATSActions/isHiringActionAllowedInState';
import UIStrings from '@/Utils/UIStrings';

interface HiringActionCellProps extends CellProps {
    column: CellParams['column'];
}

type HiringMenuItem = {
    key: string;
    menuItemLabel: string;
    disabled?: boolean;
    onSelect: () => void;
};

const HiringActionCell = ({ candidate, column }: HiringActionCellProps) => {
    const {
        assessmentId,
        assessmentStatus,
        testTypes,
        includesExperienceCriteria,
        shortlistApplicantAsync,
        interviewApplicantAsync,
        referenceCheckApplicantAsync,
        offerApplicantAsync,
        backgroundCheckApplicantAsync,
        removeApplicantAsync,
    } = useCandidatesOverviewContext();
    const { showDialog } = useActionDialogContext();
    const { hiringStatus, applicantStatus } = candidate;

    const menuItems = useMemo<HiringMenuItem[]>(() => {
        // when the assessment is closed, no actions are allowed
        if (assessmentStatus === AssessmentStatuses.Closed) {
            return [];
        }

        const handleDialog = (action: DialogEnum) => () => {
            showDialog(
                [mapFromCandidateOverviewToCandidateDialogModel(assessmentId, candidate)],
                action,
                testTypes,
                includesExperienceCriteria,
            );
        };

        const hiringActions: HiringMenuItem[] = [];

        // Remove is only allowed in Invited applicant status
        if (applicantStatus === ApplicantStatus.Invited) {
            hiringActions.push({
                key: 'remove',
                menuItemLabel: UIStrings.Remove,
                onSelect: () => removeApplicantAsync(candidate.candidateId),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.Shortlist, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'shortlist',
                menuItemLabel: UIStrings.Shortlist,
                onSelect: () => shortlistApplicantAsync(candidate.candidateId),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.InviteForInterview, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'inviteInterview',
                menuItemLabel: UIStrings.InviteForInterview,
                onSelect: handleDialog(DialogEnum.InviteForInterview),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.Interview, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'interview',
                menuItemLabel: UIStrings.Interview,
                onSelect: () => interviewApplicantAsync(candidate.candidateId),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.ReferenceCheck, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'reference',
                menuItemLabel: UIStrings.ReferenceCheck,
                onSelect: () => referenceCheckApplicantAsync(candidate.candidateId),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.Offer, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'offer',
                menuItemLabel: UIStrings.Offer,
                onSelect: () => offerApplicantAsync(candidate.candidateId),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.BackgroundCheck, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'background',
                menuItemLabel: UIStrings.BackgroundCheck,
                onSelect: () => backgroundCheckApplicantAsync(candidate.candidateId),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.Hire, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'hire',
                menuItemLabel: UIStrings.Hire,
                onSelect: handleDialog(DialogEnum.Hire),
            });
        }

        if (isHiringActionAllowedInState(HiringAction.Reject, applicantStatus, hiringStatus)) {
            hiringActions.push({
                key: 'reject',
                menuItemLabel: UIStrings.Reject,
                onSelect: handleDialog(DialogEnum.Reject),
            });
        }

        return hiringActions;
    }, [applicantStatus, hiringStatus]);

    const label = hiringStatusToString(hiringStatus);

    if (menuItems.length === 0) {
        return <TooltipTypography>{label}</TooltipTypography>;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    className="text-foreground w-full min-w-0 justify-between gap-1 px-2 font-normal"
                >
                    <TypographySmall className="min-w-0 flex-1 truncate text-left font-normal">{label}</TypographySmall>
                    <ChevronDownIcon aria-hidden />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" style={{ width: column.getSize() }} data-cy="hiringActionMenu">
                <DropdownMenuGroup>
                    {menuItems.map((item) => (
                        <DropdownMenuItem key={item.key} disabled={item.disabled} onSelect={() => item.onSelect()}>
                            {item.menuItemLabel}
                        </DropdownMenuItem>
                    ))}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    );
};

export default HiringActionCell;
